import { ADMIN_API_METHOD_TYPES } from '@lib/sdk/api/modules/admin/types.admin'
import { usePostRobot } from '@lib/sdk/utils/postrobot'
import type { File, PaginatedFileList } from '@openapi'

/**
 * Get the metadata object of an app with a given name.
 *
 * @param {object} payload - The payload object.
 * @param {string} payload.app_name - `name` value from `app.json` of the app you want to get the metadata of.
 *
 * @example
 * useAdmin().getAppConfig({ app_name: 'my-app' })
 */
export async function getAppConfig(payload: { app_name: string }): Promise<Record<string, any>> {
  const pr = await usePostRobot()
  const response = await pr.send(window.parent, ADMIN_API_METHOD_TYPES.GET_APP_CONFIG, payload)
  return <Record<string, any>>response.data
}

/**
 * Fetches a file by ID.
 *
 * @example
 * adminApi.getFile({ id: 'my-file-id' })
 */
export async function getFile(payload: { id: File['id'] }): Promise<File> {
  const pr = await usePostRobot()
  const response = await pr.send(window.parent, ADMIN_API_METHOD_TYPES.GET_FILE, payload)
  return response.data as File
}

/**
 * Lists available files.
 *
 * @example
 * adminApi.getFiles()
 */
export async function getFiles(payload: Record<string, any>): Promise<PaginatedFileList> {
  const pr = await usePostRobot()
  const response = await pr.send(window.parent, ADMIN_API_METHOD_TYPES.GET_FILES, payload)
  return response.data as PaginatedFileList
}

/**
 * Opens a file by id.
 *
 * @example
 * adminApi.open({ id: 'my-file-id' })
 */
export async function open(payload: { id: File['id'] }): Promise<void> {
  const pr = await usePostRobot()
  await pr.send(window.parent, ADMIN_API_METHOD_TYPES.OPEN, payload)
}

/**
 * Closes the already open file that was opened via api or by the user.
 *
 * @example
 * adminApi.close()
 */
export async function close(): Promise<void> {
  const pr = await usePostRobot()
  await pr.send(window.parent, ADMIN_API_METHOD_TYPES.CLOSE)
}
